import { useMemo, useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import Title from '../common/Title'
import Card from '../common/Card'
import { Amount, Button } from 'ui'
import { useIntl } from 'react-intl'
import { createDateFromISO } from 'utils/dates'
import qs from 'qs'
import { getCurrentValues, PartnerRow } from '../../utils/partnerUtils'
import Filters, { PartnerFilterForm } from './components/Filters'
import { plans } from '../../api/program'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus } from '@fortawesome/free-solid-svg-icons'
import { useMutation } from '@tanstack/react-query'
import { getPartnerExportList } from '../../api/partner'
import { useAppSelector } from '../../store/hooks'
import TablePartners from './components/TablePartners'
import { useGetProgramFilter } from '../../hooks/useGetProgramFilter'
import { toast } from 'react-toastify'
export type Level = {
   id: number
   name: string
}

export type Channel = {
   id: number
   name: string
}
type LoyaltyPlanHotel = {
   id: number
   hotelId: number
   hotelName: string
   discount: number
   contracts: number[]
}

type Photo = {
   id: number
   basePath: string
   path: string
   title: string
   galleryType: string
}

type Benefit = {
   id: number
   hotels: number[]
   name: string
}
export type LoyaltyPlan = {
   id: number
   name: string
   level: Level[]
   isActive: boolean
   hasDiscount: boolean
   primaryColor: string
   headerBackground: string
   headerFontColor: string
   hotels: LoyaltyPlanHotel[]
   photos: Photo[]
   benefits: Benefit[]
}

export type Partner = {
   id: number
   account: string
   createdAt: string
   hotelId: number
   firstName: string
   lastName: string
   email: string
   phoneNumber: string
   maritalStatus: 'MARRIED' | 'SINGLE' | 'DIVORCED' | 'WIDOWED' | 'SEPARATED'
   dateOfBirth: string
   gender: 'FEMALE' | 'MALE' | 'OTHER'
   countryOfResidence: string
   registrationChannel: string
   isActive: true
   externalId: number
   loyaltyPlans: Array<
      plans & {
         points: number
         level: string
         loyaltyPlanName: string
         expirationDate: string
         loyaltyPlanId: number
         registrationDate: string
         registrationChannel: string
         memberType: string
         segment?: string
      }
   >
   points: number
   totalRevenue: number
   latestReservationDate: string
   state?: string
   municipality?: string
   allergies?: string
   bloodType?: string
   swimmingSkill?: boolean
   passion?: string
   operaProfileSourceId?: string
   typeBlood?: string
   zipCode: string
   countryCode: string
   addressLine: string
   profiles: Array<{
      id: number
      system: string
   }>
   organization?: string
   roleInOrganization?: string
   rfc?: string
   memberCode?: string
}

type Props = {}

export const Partners = (props: Props) => {
   const intl = useIntl()
   const navigate = useNavigate()
   const location = useLocation()
   const { locale } = useAppSelector((state) => state.base)
   const [isExporting, setIsExporting] = useState(false)

   const { mutate } = useMutation({
      mutationFn: getPartnerExportList,
   })
   const { data: programs, isSuccess } = useGetProgramFilter()
   const defaultProgram =
      programs && programs.items.length > 1
         ? programs.items.find((program) => program.isDefault)?.id
         : programs?.items?.[0]?.id
   const currentValues = getCurrentValues(location.search, defaultProgram)
   let loyaltyPlan = programs?.items?.find(
      (plan) => plan.id === Number(currentValues.loyaltyPlanIds) || (defaultProgram && plan.id === defaultProgram),
   )
   const columns = useMemo(
      () => [
         {
            header: intl.formatMessage({ id: 'partners.id' }),
            accessorKey: 'externalId',
            cell: ({ row }: { row: { original: PartnerRow } }) => {
               return <Link to={`${row.original.id}/operations`}>{row.original.externalId} </Link>
            },
            enableSorting: true,
         },
         {
            header: intl.formatMessage({ id: 'partners.table.partner' }),
            accessorKey: 'fullName',
            cell: ({ row }: { row: { original: PartnerRow } }) => {
               return (
                  <Link to={`${row.original.id}/operations`}>
                     {`${row.original.firstName} ${row.original.lastName}`}
                     <div className="text-sm text-neutral-400">{row.original.email}</div>
                  </Link>
               )
            },

            enableSorting: true,
         },
         ...(loyaltyPlan?.allowPoints
            ? [
                 {
                    header: () => (
                       <div >{intl.formatMessage({ id: 'partners.table.level' })}</div>
                    ),
                    accessorKey: 'level',
                    cell: ({ row }: { row: { original: PartnerRow } }) => (
                       <div >{row.original.level}</div>
                    ),
                    enableSorting: true,
                 },
              ]
            : []),
         ...(loyaltyPlan?.allowPoints
            ? [
                 {
                    header: () => (
                       <div >{intl.formatMessage({ id: 'partners.table.channelRegister' })}</div>
                    ),
                    accessorKey: 'registrationChannel',
                    cell: ({ row }: { row: { original: PartnerRow } }) => (
                       <div >{row.original.registrationChannel || '--'}</div>
                    ),
                    enableSorting: true,
                 },
              ]
            : []),
         ...(loyaltyPlan?.allowPoints
            ? [
                 {
                    header: () => <div >{intl.formatMessage({ id: 'partners.points' })}</div>,
                    accessorKey: 'points',
                    cell: ({ row }: { row: { original: PartnerRow } }) => {
                       return (
                          <div className="text-left">
                             {row.original.points !== null ? (
                                <Amount amount={row.original.points} showCurrencySign={false} />
                             ) : (
                                '--'
                             )}
                          </div>
                       )
                    },
                    enableSorting: true,
                 },
              ]
            : []),

         {
            header: () => <div >{intl.formatMessage({ id: 'partners.income' })}</div>,
            accessorKey: 'totalRevenue',
            cell: ({ row }: { row: { original: PartnerRow } }) => (
               <div >
                  {row.original.totalRevenue !== null ? (
                     <Amount amount={row.original.totalRevenue} minimumFractionDigits={2} maximumFractionDigits={2} />
                  ) : (
                     '--'
                  )}
               </div>
            ),
            enableSorting: true,
         },
         {
            header: () => (
               <div >{intl.formatMessage({ id: 'partners.table.room.Per.Nights' })}</div>
            ),
            accessorKey: 'roomNights',
            cell: ({ row }: { row: { original: PartnerRow } }) => (
               <div >{row.original.roomNights}</div>
            ),
            enableSorting: true,
         },
         {
            header: <div >{intl.formatMessage({ id: 'partners.lastOperation' })}</div>,
            accessorKey: 'lastOperationDate',
            cell: ({ row }: { row: { original: PartnerRow } }) => {
               const isoDate = createDateFromISO(row.original.lastOperationDate).setLocale(locale)
               return (
                  <div >
                     {isoDate.isValid ? isoDate.toFormat(intl.formatMessage({ id: 'date.format' })) : '---'}
                     {isoDate.isValid ? (
                        <div className="text-sm text-neutral-400">{`${isoDate.toFormat('h:mm a')}`}</div>
                     ) : (
                        ''
                     )}
                  </div>
               )
            },
            enableSorting: true,
         },
      ],
      [intl, locale, loyaltyPlan?.allowPoints],
   )
   const onFilter = (data: PartnerFilterForm) => {
      const request = Object.assign({}, data)
      navigate(
         `?${qs.stringify(request, {
            arrayFormat: 'comma',
            allowDots: true,
            encode: false,
         })}`,
      ) //TODO: revisar si se soluciona el bug que encodea la coma https://github.com/ljharb/qs/issues/410
   }

   const onPageChange = (page: { page: number; size: number }) => {
      const request = Object.assign({}, currentValues, { ...page })
      navigate(
         `?${qs.stringify(request, {
            arrayFormat: 'comma',
            allowDots: true,
            encode: false,
         })}`,
      )
   }
   const onSort = (sort: () => Array<{ id: string; desc: boolean }>) => {
      const sortField = sort()?.[0]
      if (!sortField.id) return
      const { sort: sortFilter, ...currentValues } = getCurrentValues(location.search, defaultProgram)
      const [currentSortField, currentSortOrder] = sortFilter?.split(',') || []
      let newSort = null

      if (currentSortField !== sortField.id) {
         newSort = `${sortField.id},asc`
      } else if (currentSortOrder === 'asc') {
         newSort = `${sortField.id},desc`
      } else if (currentSortOrder === 'desc') {
         newSort = null
      }
      const request = Object.assign({}, currentValues, newSort ? { sort: newSort } : {})
      navigate(
         `?${qs.stringify(request, {
            arrayFormat: 'comma',
            allowDots: true,
            encode: false,
         })}`,
      )
   }

   const onExport = (type: string) => {
      const toastId = toast.loading('Generando archivo espere un momento...', { autoClose: false })
      const request = {
         ...getCurrentValues(location.search, defaultProgram),
         exportType: type,
      }

      setIsExporting(true)
      mutate(request, {
         onSuccess: (response) => {
            let excellName = `members.xlsx`
            const blob = new Blob([response as unknown as BlobPart], {
               type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
            const link = document.createElement('a')
            link.target = '_blank'
            link.href = window.URL.createObjectURL(blob)
            link.download = excellName
            document.body.appendChild(link)
            toast.update(toastId, {
               render: 'Exportación completada 🎉',
               type: 'success',
               isLoading: false,
               autoClose: 3000,
               position: 'bottom-center',
            })
            link.click()
            document.body.removeChild(link)
            setIsExporting(false)
            toast.update(toastId, {
               render: 'Exportación completada 🎉',
               type: 'success',
               isLoading: false,
               autoClose: 3000,
               position: 'bottom-center',
            })
         },
         onError: () => {
            setIsExporting(false)
            toast.update(toastId, {
               render: 'Error en la exportación 😢',
               type: 'error',
               isLoading: false,
               autoClose: 3000,
            })
         },
      })
   }

   const onClickCreate = () => {
      navigate(`create`)
   }

   return (
      <>
         <Title
            className="flex justify-between gap-x-2  items-center "
            value={intl.formatMessage({ id: 'titles.partners' })}
         >
            <Button onClick={onClickCreate} className={'flex gap-x-2 items-center text-[12px] mr-4 '}>
               <FontAwesomeIcon icon={faUserPlus} className={'text-gray-500 text '} />
               {intl.formatMessage({ id: 'partner.create' })}
            </Button>
         </Title>
         <div className="flex flex-col gap-y-4 mx-4">
            <Card className="py-4">
               {isSuccess && programs.items && programs.items.length > 0 && (
                  <>
                     <Filters
                        className="px-4"
                        onSubmit={onFilter}
                        defaultValues={currentValues}
                        allowPoints={loyaltyPlan?.allowPoints}
                     />

                     <TablePartners
                        currentValues={currentValues}
                        columns={columns}
                        onPageChange={onPageChange}
                        onSortChange={onSort}
                        onExport={onExport}
                        isExporting={isExporting}
                     />
                  </>
               )}
            </Card>
         </div>
      </>
   )
}
